import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, ChevronLeft, ChevronRight, RefreshCw, Save, AlertTriangle } from "lucide-react";

/**
 * WeeklyPnLCard
 * ---------------------------------------------------------------------------
 * Seven-day rolling P&L summary for the admin dashboard.
 *
 * Default window: last 7 UK calendar days ending today.
 * Navigate week-by-week with the chevrons; pick any end date with the picker.
 *
 * Revenue + COGS backdate automatically from historical orders + current
 * supplier_cost on each product. Ad-spend per day comes from whatever the
 * admin has typed in the daily card — historic days default to £0 until
 * filled in.
 */

const ukToday = () => {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London", year: "numeric", month: "2-digit", day: "2-digit",
  });
  return fmt.format(new Date());
};

const addDays = (iso, n) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
};

const fmtRange = (start, end) => {
  const fmt = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d))
      .toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
  };
  return `${fmt(start)} → ${fmt(end)}`;
};

const dayShort = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" });
};


export default function WeeklyPnLCard() {
  const [endDate, setEndDate] = useState(ukToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Each day's ad spend can be edited inline so historic weeks become accurate
  const [editingDate, setEditingDate] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingDate, setSavingDate] = useState(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data: pnl } = await api.get(`/admin/weekly-pnl?end_date=${d}`);
      setData(pnl);
    } catch (err) {
      console.error("[WeeklyPnL] load failed", err);
      toast.error("Couldn't load weekly P&L");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(endDate); }, [endDate, load]);

  const saveSpend = async (date) => {
    const num = parseFloat(editValue);
    if (Number.isNaN(num) || num < 0) {
      toast.error("Ad spend must be a positive number");
      return;
    }
    setSavingDate(date);
    try {
      await api.put("/admin/daily-pnl/ad-spend", { date, ad_spend: num });
      toast.success(`Saved £${num.toFixed(2)} for ${date}`);
      setEditingDate(null);
      await load(endDate);
    } catch (err) {
      console.error("[WeeklyPnL] save failed", err);
      toast.error("Couldn't save ad spend");
    } finally {
      setSavingDate(null);
    }
  };

  const isCurrentWeek = endDate === ukToday();
  const t = data?.totals;
  const netColour = (n) => n > 0 ? "text-[#A6FF00]" : n < 0 ? "text-[#EF4444]" : "text-[#F7FAFC]";

  // For the mini revenue bar chart we scale to the highest day in this window
  const maxDayRevenue = Math.max(1, ...(data?.days || []).map(d => d.revenue));

  return (
    <div className="pg-card p-6 mb-8" data-testid="weekly-pnl-card">
      {/* ───── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="pg-micro mb-1 flex items-center gap-2">
            <TrendingUp size={11} className="text-[#5EEAD4]" />
            // 7-Day P&amp;L
          </div>
          <h2 className="text-[#F7FAFC] pg-heading text-xl" style={{ fontFamily: "'Rajdhani'" }}>
            {data ? fmtRange(data.start_date, data.end_date) : "Loading…"}
            {isCurrentWeek && (
              <span className="text-[10px] pg-mono uppercase tracking-[0.22em] text-[#5EEAD4] align-middle ml-2">
                · This week
              </span>
            )}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setEndDate(addDays(endDate, -7))}
                  className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5"
                  data-testid="weekly-prev" title="Previous 7 days">
            <ChevronLeft size={14} />
          </button>
          <input type="date" value={endDate} max={ukToday()}
                 onChange={(e) => setEndDate(e.target.value)}
                 className="pg-input text-xs py-1.5 px-2"
                 data-testid="weekly-end-picker" />
          <button type="button" onClick={() => setEndDate(addDays(endDate, +7))}
                  disabled={isCurrentWeek}
                  className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 disabled:opacity-30"
                  data-testid="weekly-next" title="Next 7 days">
            <ChevronRight size={14} />
          </button>
          <button type="button" onClick={() => load(endDate)} disabled={loading}
                  className="pg-btn-ghost text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 ml-1"
                  data-testid="weekly-refresh" title="Refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {!data ? (
        <div className="text-[#9CA8B8] pg-mono py-8 text-center text-sm">Loading…</div>
      ) : (
        <>
          {/* ───── Totals ─────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <Tile label="Revenue" value={t.revenue} colour="lime"
                  sub={`${t.orders_count} order${t.orders_count === 1 ? "" : "s"}`} />
            <Tile label="COGS" value={t.cogs} colour="cyan"
                  sub="cost of vials sold" negative />
            <Tile label="Gross Profit" value={t.gross_profit} colour="lime"
                  sub="revenue − COGS" />
            <Tile label="Ad Spend" value={t.ad_spend} colour="cyan"
                  sub="sum of 7 days" negative />
            <Tile label="Net Profit" value={t.net_profit}
                  sub={`${t.net_margin_pct.toFixed(1)}% margin`}
                  big colourOverride={netColour(t.net_profit)} />
          </div>

          {/* ───── Per-day breakdown with mini bars ──────────────── */}
          <div className="border border-[#1E2E44] rounded-sm overflow-hidden">
            <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-[#0B1220] text-[10px] pg-mono uppercase tracking-[0.16em] text-[#9CA8B8]">
              <div className="col-span-2">Day</div>
              <div className="col-span-3">Revenue</div>
              <div className="col-span-2 text-right">COGS</div>
              <div className="col-span-2 text-right">Ad Spend</div>
              <div className="col-span-3 text-right">Net</div>
            </div>
            {data.days.map((d) => {
              const w = `${(d.revenue / maxDayRevenue) * 100}%`;
              const isEditing = editingDate === d.date;
              return (
                <div key={d.date}
                     className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center border-t border-[#1E2E44] hover:bg-[#0B1220]/40"
                     data-testid={`weekly-day-${d.date}`}>
                  <div className="col-span-2">
                    <div className="text-[#F7FAFC] text-sm">{dayShort(d.date)}</div>
                    <div className="text-[10px] text-[#9CA8B8] pg-mono">{d.date.slice(5)}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[#A6FF00] pg-mono text-sm w-16">£{d.revenue.toFixed(2)}</span>
                      <div className="flex-1 h-1 bg-[#0B1220] rounded-sm overflow-hidden">
                        <div className="h-full bg-[#A6FF00]/60" style={{ width: w }} />
                      </div>
                    </div>
                    <div className="text-[10px] text-[#9CA8B8]">{d.orders_count} order{d.orders_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="col-span-2 text-right text-[#00C2D6] pg-mono text-sm">
                    {d.cogs > 0 ? `−£${d.cogs.toFixed(2)}` : "—"}
                  </div>
                  <div className="col-span-2 text-right">
                    {isEditing ? (
                      <div className="flex items-center justify-end gap-1">
                        <span className="text-[#9CA8B8] text-xs">£</span>
                        <input type="number" step="0.01" min="0" autoFocus
                               value={editValue}
                               onChange={(e) => setEditValue(e.target.value)}
                               onKeyDown={(e) => {
                                 if (e.key === "Enter") saveSpend(d.date);
                                 if (e.key === "Escape") setEditingDate(null);
                               }}
                               className="w-20 bg-[#0B1220] border border-[#A6FF00]/40 rounded-sm text-[#F7FAFC] pg-mono text-sm py-0.5 px-1.5 text-right outline-none"
                               data-testid={`weekly-spend-input-${d.date}`} />
                        <button type="button" onClick={() => saveSpend(d.date)}
                                disabled={savingDate === d.date}
                                className="text-[#A6FF00] hover:text-[#5EEAD4] disabled:opacity-50"
                                data-testid={`weekly-spend-save-${d.date}`}>
                          <Save size={12} />
                        </button>
                      </div>
                    ) : (
                      <button type="button"
                              onClick={() => { setEditingDate(d.date); setEditValue(String(d.ad_spend ?? 0)); }}
                              className="text-[#9CA8B8] hover:text-[#A6FF00] pg-mono text-sm group"
                              data-testid={`weekly-spend-edit-${d.date}`}
                              title="Click to edit">
                        {d.ad_spend > 0 ? `−£${d.ad_spend.toFixed(2)}` : (
                          <span className="opacity-50 group-hover:opacity-100">+ add</span>
                        )}
                      </button>
                    )}
                  </div>
                  <div className={`col-span-3 text-right pg-mono text-sm ${netColour(d.net_profit)}`}>
                    {d.net_profit > 0 ? `£${d.net_profit.toFixed(2)}` : `−£${Math.abs(d.net_profit).toFixed(2)}`}
                    <div className="text-[10px] text-[#9CA8B8]">{d.net_margin_pct.toFixed(1)}% margin</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ───── Footnotes ─────────────────────────────────────── */}
          {t.missing_cost_skus && t.missing_cost_skus.length > 0 && (
            <div className="mt-4 flex items-start gap-2 p-3 rounded-sm bg-[#EF4444]/[0.08] border border-[#EF4444]/30">
              <AlertTriangle size={14} className="text-[#EF4444] shrink-0 mt-0.5" />
              <div className="text-[11px] text-[#FCA5A5] leading-relaxed">
                <strong>COGS incomplete this week.</strong> Missing supplier_cost for{" "}
                <strong>{t.missing_cost_skus.join(", ")}</strong>. Set under Admin → Products → Edit.
              </div>
            </div>
          )}

          <div className="mt-4 text-[10px] pg-mono text-[#9CA8B8] uppercase tracking-[0.18em] flex flex-wrap gap-4">
            <span>UK timezone</span>
            <span>·</span>
            <span>Avg order: £{t.avg_order_value.toFixed(2)}</span>
            <span>·</span>
            <span>Excludes test orders</span>
            <span>·</span>
            <span>Tap a day's ad-spend to edit</span>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value, colour = "lime", sub, big = false, negative = false, colourOverride }) {
  const limeOrCyan = colour === "lime" ? "text-[#A6FF00]" : "text-[#00C2D6]";
  const finalColour = colourOverride || limeOrCyan;
  const sign = negative && value > 0 ? "−" : "";
  return (
    <div className="border border-[#1E2E44] rounded-sm p-3">
      <div className="pg-micro mb-1">{label}</div>
      <div className={`pg-display ${big ? "text-3xl" : "text-2xl"} ${finalColour}`}
           style={{ fontFamily: "'Exo 2'" }}>
        {sign}£{Math.abs(value).toFixed(2)}
      </div>
      {sub && <div className="text-[10px] text-[#9CA8B8] mt-1">{sub}</div>}
    </div>
  );
}
